import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dimensions, Image, StyleSheet, Text, Pressable, View } from 'react-native';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { MaterialIcons } from "@expo/vector-icons";

import CardImageContainer from './CardImageContainer';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import DataOps from '../../../../data/DataOps';
import { getCampTypeColor, getCampTypeIconName, getCampTypeText } from '../../../../data/CampTypes';
import ShareModal from '../../../../components/general/ShareModal';
import ReviewDAO from '../../../../data/ReviewDAO';

// Use same width as SessionCard for consistency
const WIDTH = Dimensions.get('window').width * 0.166;

const CampCard = ({ camp, isMapViewStyle = false }) => {
    const navigate = useNavigate();
    const {
        user: user,
        likedCamps: likedCamps,
        setLikedCamps: setLikedCamps
    } = useContext(GlobalStateContext);

    const isLiked = likedCamps.has(camp.campId);
    const [isMapRowHovered, setIsMapRowHovered] = useState(false);
    const [isEmailRowHovered, setIsEmailRowHovered] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    const coachCount = (camp.campCoaches ? camp.campCoaches.length : (camp.campInstructors ? Object.keys(camp.campInstructors).length : 0));
    const followersCount = typeof camp.followers === 'number' ? camp.followers : (camp.campFollowers ? camp.campFollowers.length : 0);
    const [liveRating, setLiveRating] = useState(
        typeof camp.rating === 'number' ? camp.rating : 0
    );
    const [liveReviewCount, setLiveReviewCount] = useState(
        typeof camp.reviewCount === 'number' ? camp.reviewCount : 0
    );

    useEffect(() => {
        let isCancelled = false;

        const loadReviewSummary = async () => {
            try {
                const fallbackRating = typeof camp.rating === 'number' ? camp.rating : 0;
                const fallbackReviewCount = typeof camp.reviewCount === 'number' ? camp.reviewCount : 0;

                if (!camp?.campId) {
                    if (!isCancelled) {
                        setLiveRating(fallbackRating);
                        setLiveReviewCount(fallbackReviewCount);
                    }
                    return;
                }

                const response = await ReviewDAO.getCampReviews(camp.campId, 1);
                const summary = response?.summary || {};
                const resolvedRating = typeof summary.averageRating === 'number' ? summary.averageRating : fallbackRating;
                const resolvedReviewCount = typeof summary.reviewCount === 'number' ? summary.reviewCount : fallbackReviewCount;

                if (!isCancelled) {
                    setLiveRating(resolvedRating);
                    setLiveReviewCount(resolvedReviewCount);
                }
            } catch {
                if (!isCancelled) {
                    setLiveRating(typeof camp.rating === 'number' ? camp.rating : 0);
                    setLiveReviewCount(typeof camp.reviewCount === 'number' ? camp.reviewCount : 0);
                }
            }
        };

        loadReviewSummary();
        return () => {
            isCancelled = true;
        };
    }, [camp?.campId, camp?.rating, camp?.reviewCount]);

    const updateLikedCamps = () => {
        let newLikedCamps = new Set([...likedCamps]);
        if (isLiked) {
            newLikedCamps.delete(camp.campId);
        } else {
            newLikedCamps.add(camp.campId);
        }
        setLikedCamps(newLikedCamps);
        return newLikedCamps;
    };

    const updateCampFollowers = async () => {
        const campInfo = await DataOps.fetchDataFromFirebase('camp', camp.campId);
        const campFollowers = campInfo.campFollowers || [];

        let newCampFollowers = new Set([...campFollowers]);
        if (isLiked) {
            newCampFollowers.delete(user.userId);
        } else {
            newCampFollowers.add(user.userId);
        }

        await DataOps.saveCustomDataToFirebase(
            'camp', camp.campId, { ...campInfo, campFollowers: [...newCampFollowers], followers: newCampFollowers.size }, true
        );
    };

    const handleLikePress = () => {
        const newLikedCamps = updateLikedCamps();
        updateCampFollowers();

        if (user) {
            DataOps.saveCustomDataToFirebase(
                'user', user.userId, { ...user, likedCamps: [...newLikedCamps] }, true
            );
        }
    };

    const handleCampPress = (e?: any) => {
        try {
            if ((window as any).__coachModalOpen) {
                if (e?.stopPropagation) e.stopPropagation();
                if (e?.preventDefault) e.preventDefault();
                return;
            }
        } catch { }
        navigate(`/camps/${camp.campId}`);
    };

    const handleMapPress = () => {
        if (!camp.campAddress) return;

        const encodedAddress = encodeURIComponent(camp.campAddress);
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

        window.open(mapsUrl, '_blank');
    };

    const handleEmailPress = () => {
        if (!camp.contactEmail) return;

        const subject = encodeURIComponent(`Inquiry about ${camp.campName}`);
        const body = encodeURIComponent(`Hello,\n\nI'm interested in learning more about ${camp.campName} and the upcoming sessions you offer.\n\nThank you!`);

        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${camp.contactEmail}&su=${subject}&body=${body}`;

        window.open(gmailUrl, '_blank');
    };

    const getFormattedAddress = (address) => {
        if (!address) return [camp.street, camp.city, camp.state, camp.zipCode].filter(Boolean).join(', ');
        return address;
    };

    const handleSharePress = (e) => {
        e.stopPropagation();
        setShowShareModal(true);
    };

    const getShareUrl = () => {
        return `${window.location.origin}/camps/${camp.campId}`;
    };

    const getCampDescription = () => {
        const location = [camp.city, camp.state].filter(Boolean).join(', ');
        const category = getCampTypeText(camp.campCategory);
        return `${category} camp in ${location} · ${camp.activeSessionCount || 0} sessions available`;
    };

    const categoryIconName = getCampTypeIconName(camp.campCategory);
    const categoryBannerColor = getCampTypeColor(camp.campCategory);
    const activeSessionCount = camp.activeSessionCount ?? 0;

    return (
        <>
            <Pressable
                style={[styles.card, isMapViewStyle && { marginBottom: 0, marginRight: 0 }]}
                onPress={handleCampPress}
            >
                {/* Camp Image */}
                <View style={styles.imageContainer}>
                    <CardImageContainer images={camp.campImageUrls} />

                    {/* Action buttons overlay */}
                    <View style={styles.actionButtonsOverlay}>
                        {/* Share button */}
                        <Pressable style={styles.actionButton} onPress={handleSharePress}>
                            <ShareIcon style={{ fontSize: 20, color: 'white' }} />
                        </Pressable>

                        {/* Like button */}
                        <Pressable style={styles.actionButton} onPress={(e) => { e.stopPropagation(); handleLikePress(); }}>
                            {isLiked ? (
                                <FavoriteIcon style={{ fontSize: 20, color: '#FF6B6B' }} />
                            ) : (
                                <FavoriteBorderIcon style={{ fontSize: 20, color: 'white' }} />
                            )}
                        </Pressable>
                    </View>

                    <View style={[
                        styles.sessionsBadge,
                        { backgroundColor: activeSessionCount > 0 ? '#3C8248' : '#000000' }
                    ]}>
                        <Text style={styles.sessionsBadgeText}>
                            {
                                activeSessionCount === 0
                                    ? 'Offseason'
                                    : activeSessionCount === 1
                                        ? '1 Session'
                                        : activeSessionCount + ' Sessions'
                            }
                        </Text>
                    </View>

                    {/* Fully Verified badge moved to header */}
                </View>

                {/* Header with camp name and category */}
                <View style={styles.header}>
                    {/* Badges row */}
                    <View style={styles.badgesRow}>
                        <View style={[styles.badge, styles.typeBadge, { backgroundColor: categoryBannerColor, borderColor: categoryBannerColor }]}>
                            <MaterialIcons name={categoryIconName as any} style={{ fontSize: 12, color: '#FFFFFF' }} />
                            <Text style={[styles.badgeText, { color: '#FFFFFF' }]}>
                                {getCampTypeText(camp.campCategory)}
                            </Text>
                        </View>
                        {camp.fullyVerified && (
                            <View style={[styles.badge, styles.verifiedBadge]}>
                                <VerifiedIcon style={{ fontSize: 12, color: '#6b4e00' }} />
                                <Text style={[styles.badgeText, styles.verifiedText]}>Verified</Text>
                            </View>
                        )}
                    </View>

                    <Text style={styles.titleText} numberOfLines={2}>{camp.campName}</Text>
                </View>

                {/* Content Container */}
                <View style={styles.contentContainer}>
                    {/* Address */}
                    <Pressable
                        onPress={handleMapPress}
                        onHoverIn={() => setIsMapRowHovered(true)}
                        onHoverOut={() => setIsMapRowHovered(false)}
                        style={[
                            styles.infoTextContainer,
                            isMapRowHovered && styles.hoveredMapRow
                        ]}
                    >
                        <LocationOnIcon style={{ fontSize: 18, color: isMapRowHovered ? '#2C67D6' : 'black' }} />
                        <Text style={[
                            styles.infoText,
                            styles.clickableText,
                            isMapRowHovered && styles.clickableTextHovered
                        ]} numberOfLines={2}>
                            {getFormattedAddress(camp.campAddress)}
                        </Text>
                    </Pressable>

                    {/* Rating + Reviews */}
                    <View style={[styles.infoTextContainer, styles.metricRow]}>
                        <View style={styles.metricGroup}>
                            <StarIcon style={{ fontSize: 18, color: '#FFB000' }} />
                            <Text style={styles.metricText}>
                                {liveReviewCount > 0 ? `${liveRating.toFixed(1)} rating` : 'No ratings yet'}
                            </Text>
                        </View>
                        <Text style={styles.metricSeparator}>•</Text>
                        <View style={styles.metricGroup}>
                            <RateReviewIcon style={{ fontSize: 16, color: '#666' }} />
                            <Text style={styles.metricText}>
                                {liveReviewCount} {liveReviewCount === 1 ? 'review' : 'reviews'}
                            </Text>
                        </View>
                    </View>

                    {/* Coaches + Followers */}
                    <View style={[styles.infoTextContainer, styles.metricRow]}>
                        <View style={styles.metricGroup}>
                            <GroupIcon style={{ fontSize: 18, color: 'black' }} />
                            <Text style={styles.metricText}>
                                {coachCount} {coachCount === 1 ? 'coach' : 'coaches'}
                            </Text>
                        </View>
                        <Text style={styles.metricSeparator}>•</Text>
                        <View style={styles.metricGroup}>
                            <PeopleIcon style={{ fontSize: 18, color: '#4CAF50' }} />
                            <Text style={styles.metricText}>
                                {followersCount} {followersCount === 1 ? 'follower' : 'followers'}
                            </Text>
                        </View>
                    </View>

                    {/* Contact Email */}
                    {camp.contactEmail && (
                        <Pressable
                            onPress={handleEmailPress}
                            onHoverIn={() => setIsEmailRowHovered(true)}
                            onHoverOut={() => setIsEmailRowHovered(false)}
                            style={[
                                styles.infoTextContainer,
                                isEmailRowHovered && styles.hoveredEmailRow
                            ]}
                        >
                            <EmailIcon style={{ fontSize: 18, color: isEmailRowHovered ? '#2C67D6' : 'black' }} />
                            <Text style={[
                                styles.infoText,
                                styles.clickableText,
                                isEmailRowHovered && styles.clickableTextHovered
                            ]}>
                                {camp.contactEmail}
                            </Text>
                        </Pressable>
                    )}
                </View>
            </Pressable>

            {/* Share Modal */}
            <ShareModal
                visible={showShareModal}
                onClose={() => setShowShareModal(false)}
                title={camp.campName}
                description={getCampDescription()}
                imageUrl={camp.campImageUrls?.[0]}
                shareUrl={getShareUrl()}
            />
        </>
    );
};

const styles = StyleSheet.create({
    card: {
        backgroundColor: 'white',
        borderRadius: 32,
        margin: 8,
        padding: 0,
        width: WIDTH,
        minHeight: 400,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#f0f0f0',
    },
    imageContainer: {
        position: 'relative',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        overflow: 'hidden',
    },
    actionButtonsOverlay: {
        position: 'absolute',
        top: 12,
        right: 12,
        flexDirection: 'row',
        gap: 8,
    },
    actionButton: {
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: 20,
        padding: 8,
        zIndex: 10,
    },
    sessionsBadge: {
        position: 'absolute',
        top: 12,
        left: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        zIndex: 10,
        backgroundColor: '#3C8248',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 2,
    },
    verifiedRibbon: {
        position: 'absolute',
        top: 12,
        right: 56,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        backgroundColor: '#FFD86B',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#F0C24A',
        zIndex: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 2,
    },
    verifiedRibbonText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#6b4e00',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    sessionsBadgeText: {
        fontSize: 11,
        fontWeight: '700',
        color: 'white',
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },
    header: {
        paddingHorizontal: 14,
        paddingTop: 20,
        paddingBottom: 6,
    },
    titleText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#333',
        lineHeight: 19,
        marginBottom: 2,
    },
    badgesRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 6,
        marginLeft: -5,
        flexWrap: 'wrap',
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        borderWidth: 1,
    },
    verifiedBadge: {
        backgroundColor: '#FFD86B',
        borderColor: '#F0C24A',
    },
    typeBadge: {
        backgroundColor: '#F5F7FA',
        borderColor: '#E7ECF3',
    },
    badgeText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#555',
        textTransform: 'capitalize',
    },
    verifiedText: {
        color: '#6b4e00',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    likeButton: {
        padding: 4,
    },
    contentContainer: {
        paddingHorizontal: 14,
        paddingBottom: 12,
        flex: 1,
    },
    infoTextContainer: {
        flexDirection: 'row',
        marginBottom: 6,
        alignItems: 'center',
        paddingVertical: 2,
        paddingHorizontal: 2,
        marginHorizontal: -2,
        borderRadius: 4,
        minHeight: 24,
    },
    metricRow: {
        flexWrap: 'wrap',
        columnGap: 8,
        rowGap: 4,
    },
    metricGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: 0,
    },
    metricText: {
        fontSize: 12,
        marginLeft: 6,
        color: '#333',
    },
    metricSeparator: {
        fontSize: 12,
        color: '#8a8a8a',
        marginHorizontal: 2,
        lineHeight: 16,
    },
    infoText: {
        fontSize: 12,
        fontWeight: 'normal',
        marginLeft: 6,
        flex: 1,
        color: '#333',
    },
    clickableText: {
        color: '#333',
    },
    clickableTextHovered: {
        color: '#2C67D6',
    },
    hoveredMapRow: {
        backgroundColor: '#F0F7FF',
        transform: [{ scale: 1.02 }],
    },
    hoveredEmailRow: {
        backgroundColor: '#F0F7FF',
        transform: [{ scale: 1.02 }],
    },
    sessionsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: '#f0f0f0',
    },
    sessionsInfo: {
        flex: 1,
    },
    sessionsCountText: {
        fontSize: 20,
        fontWeight: '700',
        color: '#2C67D6',
        lineHeight: 24,
    },
    sessionsLabelText: {
        fontSize: 12,
        color: '#666',
        marginTop: 1,
    },
    availabilityBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    availabilityText: {
        fontSize: 11,
        fontWeight: '600',
        color: 'white',
        textTransform: 'uppercase',
    },
});

export default CampCard;